//src/types/Store/Aircraft/WorkOrder.ts

// A work order is the container between "something needs doing" and "aircraft
// returned to service". It is NOT a logbook record — the signed maintenance
// entries stay the record of the work. In-house work only; outside shops are
// recorded as an invoice on the ledger with their paperwork attached.
//
// Stored at StoresCustomers/{school}/Maintenance/WorkOrders/Orders/{id}.
// The numbering counter is the WorkOrders doc itself ({ nextNumber }).
// Approvals: …/Orders/{id}/Approvals/{approvalId} (append-only).
//
// Pricing (labor + parts per task, tax, approval threshold) is computed in
// src/utils/maintenance/workOrderPricing.ts — the ONE place the math lives.

import type { Timestamp } from 'firebase/firestore'

export type WorkOrderStatus =
  | 'open'
  | 'in_progress'
  | 'awaiting_parts'
  | 'ready_for_signoff'
  | 'closed'
  | 'cancelled'

export const WORK_ORDER_STATUS_OPTIONS: {
  value: WorkOrderStatus
  label: string
  color: string
  icon: string
}[] = [
  { value: 'open', label: 'Open', color: 'blue', icon: 'mdi-clipboard-outline' },
  { value: 'in_progress', label: 'In Progress', color: 'orange', icon: 'mdi-wrench' },
  {
    value: 'awaiting_parts',
    label: 'Awaiting Parts',
    color: 'deep-orange',
    icon: 'mdi-package-variant'
  },
  {
    value: 'ready_for_signoff',
    label: 'Ready for Sign-off',
    color: 'teal',
    icon: 'mdi-signature-freehand'
  },
  { value: 'closed', label: 'Closed', color: 'green', icon: 'mdi-check-circle' },
  { value: 'cancelled', label: 'Cancelled', color: 'grey', icon: 'mdi-cancel' }
]

/** Statuses a work order can still take work in (squawks can be added). */
export const OPEN_WORK_ORDER_STATUSES: WorkOrderStatus[] = [
  'open',
  'in_progress',
  'awaiting_parts',
  'ready_for_signoff'
]

/**
 * Statuses that commit to doing (or buying parts for) the work. Moving into
 * one of these needs the estimate approved when it is over the school's limit.
 */
export const WORK_STARTED_STATUSES: WorkOrderStatus[] = ['in_progress', 'awaiting_parts']

/** Statuses a person picks by hand. Closed and Cancelled have their own buttons. */
export const SELECTABLE_WORK_ORDER_STATUSES = WORK_ORDER_STATUS_OPTIONS.filter((o) =>
  OPEN_WORK_ORDER_STATUSES.includes(o.value)
)

export type WorkOrderTaskStatus = 'open' | 'done' | 'deferred' | 'cancelled'

export const WORK_ORDER_TASK_STATUS_OPTIONS: {
  value: WorkOrderTaskStatus
  label: string
  color: string
}[] = [
  { value: 'open', label: 'Open', color: 'orange' },
  { value: 'done', label: 'Done', color: 'green' },
  { value: 'deferred', label: 'Deferred', color: 'deep-orange' },
  { value: 'cancelled', label: 'Cancelled', color: 'grey' }
]

export type WorkOrderTaskSource = 'squawk' | 'manual'

/** One block of a mechanic's time on a task. */
export interface WorkOrderLaborLine {
  id: string
  mechanicId: string
  mechanicName: string
  date: Timestamp | null
  hours: number
  /** $/hour, copied from the school's rates when the line is added; editable. */
  rate: number
  note: string
}

/**
 * One part (or consumable) used on a task — typed in by hand, or issued from
 * stock ("From stock": the parts inventory). A line from stock keeps which
 * stock record it came from and the part's serial / lot number and release
 * paperwork; its part number and quantity can't be edited, and removing it
 * returns the part to stock (utils/maintenance/workOrderData.ts).
 *
 * `installs`: the signed maintenance entries that installed the part from
 * this line ("From work order" in the entry's parts). Added when the entry is
 * signed, taken off when it's voided. An installed line can't be removed (nor
 * its task, nor the work order cancelled) — the part is on the aircraft.
 */
export interface WorkOrderPartLine {
  id: string
  partNumber: string
  description: string
  quantity: number
  /** What the part cost the school, each. */
  unitCost: number
  /** Decimal, 0.2 = 20% over cost. Defaults from the school's setting. */
  markup: number
  taxable: boolean
  /** The stock record it was issued from ('' = typed by hand). */
  stockId: string
  serialNumber: string
  lotNumber: string
  /** Release / traceability, e.g. "FAA 8130-3 #A12345". */
  traceability: string
  installs: WorkOrderLineInstall[]
}

/** One install of a part line's part, by a signed maintenance entry. */
export interface WorkOrderLineInstall {
  /** The maintenance entry. */
  entryId: string
  /** Its part change (PartChange.id). */
  changeId: string
}

/** Installed on the aircraft by a signed maintenance entry. */
export const isLineInstalled = (p: Pick<WorkOrderPartLine, 'installs'>) => p.installs.length > 0

/** Lines can't be removed once installed; this says why. */
export const INSTALLED_LINE_MESSAGE =
  'is installed on the aircraft by a signed maintenance entry. Void that entry first.'

/** Markup as the percent people type (0.2 → 20). */
export const markupToPercent = (markup: number) => Math.round((markup || 0) * 1000) / 10

/** A typed percent as the stored markup (20 → 0.2). */
export const markupFromPercent = (percent: number) => (Number(percent) || 0) / 100

export interface WorkOrderTask {
  id: string
  sourceType: WorkOrderTaskSource
  /** The squawk this task came from ('' for a manual task). */
  squawkId: string
  ticketNumber: string
  description: string
  /** Whether the squawk was a grounding squawk when it was added. */
  groundAlert: boolean
  status: WorkOrderTaskStatus
  correctiveAction: string
  /** Required when status is 'deferred'. */
  deferReason: string
  addedBy: string
  addedAt: Timestamp
  labor: WorkOrderLaborLine[]
  parts: WorkOrderPartLine[]
}

/**
 * Who pays. 'aircraft' posts to the aircraft's ledger (its owners, through
 * their lease payouts; the school itself for a school-owned aircraft).
 * 'person' posts a charge to a member's account.
 */
export type WorkOrderBillToKind = 'aircraft' | 'person'

export interface WorkOrderBillTo {
  kind: WorkOrderBillToKind
  /** The aircraft's document id, or the member's account id. */
  accountId: string
  /** Display name at the time it was chosen (tail number or person). */
  name: string
}

/**
 * How the paying party gave their OK. Every approval is electronically signed
 * (password or Google) by the Owner / Admin / Finance person recording it.
 * 'signature' — the payer drawing their own signature on screen — is kept for
 * when a signature pad is in use; it is offered but disabled until then.
 */
export type WorkOrderApprovalMethod = 'in_person' | 'verbal' | 'email' | 'internal' | 'signature'

export const WORK_ORDER_APPROVAL_METHOD_OPTIONS: {
  value: WorkOrderApprovalMethod
  label: string
  /** Shown but not selectable yet. */
  disabled?: boolean
}[] = [
  { value: 'in_person', label: 'In person (staff records it)' },
  { value: 'verbal', label: 'By phone' },
  { value: 'email', label: 'Email or text' },
  { value: 'internal', label: 'School approval (school-owned aircraft)' },
  { value: 'signature', label: 'Signed on a signature pad (coming later)', disabled: true }
]

/** The latest approval, kept on the work order. The full record is in Approvals/. */
export interface WorkOrderApprovalSummary {
  approvalId: string
  /** Approved up to this many dollars. */
  amount: number
  approvedByName: string
  method: WorkOrderApprovalMethod
  /** billToKey() of the party it was approved for — a new bill-to voids it. */
  billToKey: string
  recordedBy: string
  recordedAt: Timestamp | null
}

/** The full approval record (Approvals subcollection). Never edited or deleted. */
export interface WorkOrderApproval {
  id: string
  amount: number
  approvedByName: string
  method: WorkOrderApprovalMethod
  /** PNG data URL, only for method 'signature' (signature pad — not in use yet). */
  signatureDataUrl: string
  notes: string
  billToKey: string
  billToName: string
  recordedBy: string
  recordedByName: string
  recordedAt: Timestamp | null
  /** The electronic signature of the person who recorded it (AC 120-78B). */
  signedById: string
  signedByName: string
  signedByEmail: string
  signedAt: Timestamp | null
}

export type WorkOrderBillingStatus = 'unbilled' | 'posted' | 'no_charge'

export interface WorkOrderBilling {
  status: WorkOrderBillingStatus
  /** The ledger entry id (same on both legs). '' until posted. */
  entryId: string
  labor: number
  parts: number
  tax: number
  total: number
  billToKey: string
  billToName: string
  postedBy: string
  postedAt: Timestamp | null
}

export function createUnbilled(): WorkOrderBilling {
  return {
    status: 'unbilled',
    entryId: '',
    labor: 0,
    parts: 0,
    tax: 0,
    total: 0,
    billToKey: '',
    billToName: '',
    postedBy: '',
    postedAt: null
  }
}

export interface WorkOrder {
  id: string
  woNumber: string
  aircraftId: string
  tailNumber: string
  status: WorkOrderStatus
  description: string
  tasks: WorkOrderTask[]
  openedBy: string
  openedAt: Timestamp | null
  updatedBy: string
  updatedAt: Timestamp | null
  closedBy: string
  closedAt: Timestamp | null
  cancelReason: string
  billTo: WorkOrderBillTo | null
  /** Parts tax rate (decimal) copied from the school when opened; null on older WOs. */
  taxRate: number | null
  approval: WorkOrderApprovalSummary | null
  billing: WorkOrderBilling
}

/* ── School settings (Store.workOrderSettings) ────────────────────────────── */

export interface WorkOrderSettings {
  /** $/hour when a mechanic has no rate of their own. */
  shopRate: number
  /** Per-mechanic $/hour, by account id. Overrides the shop rate. */
  mechanicRates: Record<string, number>
  /** Estimates over this many dollars need the paying party's approval. */
  approvalThreshold: number
  /** Decimal, 0.0925 = 9.25%. Applies to taxable parts only. */
  partsTaxRate: number
  /** Decimal default markup for new part lines. */
  defaultPartsMarkup: number
}

export function createWorkOrderSettings(
  init: Partial<WorkOrderSettings> | null | undefined = {}
): WorkOrderSettings {
  const { mechanicRates, ...rest } = init ?? {}
  return {
    shopRate: 0,
    approvalThreshold: 250,
    partsTaxRate: 0,
    defaultPartsMarkup: 0,
    ...rest,
    mechanicRates: { ...(mechanicRates ?? {}) }
  }
}

/* ── Reading ──────────────────────────────────────────────────────────────── */

const num = (v: unknown, fallback = 0) =>
  typeof v === 'number' && Number.isFinite(v) ? v : fallback

/** Older lines have no stock fields: they were typed by hand. */
function partLineFromFirestore(p: Record<string, any>): WorkOrderPartLine {
  return {
    ...(p as WorkOrderPartLine),
    stockId: p.stockId ?? '',
    serialNumber: p.serialNumber ?? '',
    lotNumber: p.lotNumber ?? '',
    traceability: p.traceability ?? '',
    installs: Array.isArray(p.installs) ? p.installs : []
  }
}

function taskFromFirestore(t: Record<string, any>): WorkOrderTask {
  return {
    ...(t as WorkOrderTask),
    correctiveAction: t.correctiveAction ?? '',
    deferReason: t.deferReason ?? '',
    labor: Array.isArray(t.labor) ? t.labor : [],
    parts: Array.isArray(t.parts) ? t.parts.map(partLineFromFirestore) : []
  }
}

export function workOrderFromFirestore(id: string, data: Record<string, any>): WorkOrder {
  return {
    id,
    woNumber: data.woNumber ?? '',
    aircraftId: data.aircraftId ?? '',
    tailNumber: data.tailNumber ?? '',
    status: (data.status ?? 'open') as WorkOrderStatus,
    description: data.description ?? '',
    tasks: Array.isArray(data.tasks) ? data.tasks.map(taskFromFirestore) : [],
    openedBy: data.openedBy ?? '',
    openedAt: data.openedAt ?? null,
    updatedBy: data.updatedBy ?? '',
    updatedAt: data.updatedAt ?? null,
    closedBy: data.closedBy ?? '',
    closedAt: data.closedAt ?? null,
    cancelReason: data.cancelReason ?? '',
    billTo: data.billTo?.kind ? (data.billTo as WorkOrderBillTo) : null,
    taxRate: typeof data.taxRate === 'number' ? data.taxRate : null,
    approval: data.approval?.approvalId ? (data.approval as WorkOrderApprovalSummary) : null,
    billing: { ...createUnbilled(), ...(data.billing ?? {}) }
  }
}

export function approvalFromFirestore(id: string, data: Record<string, any>): WorkOrderApproval {
  return {
    id,
    amount: num(data.amount),
    approvedByName: data.approvedByName ?? '',
    method: (data.method ?? 'verbal') as WorkOrderApprovalMethod,
    signatureDataUrl: data.signatureDataUrl ?? '',
    notes: data.notes ?? '',
    billToKey: data.billToKey ?? '',
    billToName: data.billToName ?? '',
    recordedBy: data.recordedBy ?? '',
    recordedByName: data.recordedByName ?? '',
    recordedAt: data.recordedAt ?? null,
    signedById: data.signedById ?? '',
    signedByName: data.signedByName ?? '',
    signedByEmail: data.signedByEmail ?? '',
    signedAt: data.signedAt ?? null
  }
}

/* ── Helpers ──────────────────────────────────────────────────────────────── */

export function isWorkOrderOpen(wo: Pick<WorkOrder, 'status'>): boolean {
  return OPEN_WORK_ORDER_STATUSES.includes(wo.status)
}

/** Billing can still change: not cancelled and not yet posted. */
export function isWorkOrderBillable(wo: Pick<WorkOrder, 'status' | 'billing'>): boolean {
  return wo.status !== 'cancelled' && wo.billing.status === 'unbilled'
}

export function billToKey(b: WorkOrderBillTo | null | undefined): string {
  return b ? `${b.kind}:${b.accountId}` : ''
}

export function workOrderStatusOption(status: WorkOrderStatus) {
  return WORK_ORDER_STATUS_OPTIONS.find((o) => o.value === status) ?? WORK_ORDER_STATUS_OPTIONS[0]
}

export function taskStatusOption(status: WorkOrderTaskStatus) {
  return (
    WORK_ORDER_TASK_STATUS_OPTIONS.find((o) => o.value === status) ??
    WORK_ORDER_TASK_STATUS_OPTIONS[0]
  )
}

export function approvalMethodLabel(method: WorkOrderApprovalMethod): string {
  return WORK_ORDER_APPROVAL_METHOD_OPTIONS.find((o) => o.value === method)?.label ?? method
}

/** Whether the work order may be closed (returned to service) now. */
export function canCloseWorkOrder(wo: WorkOrder): { ok: boolean; reason: string } {
  if (!isWorkOrderOpen(wo)) return { ok: false, reason: 'This work order is already finished.' }
  if (!wo.tasks.length) return { ok: false, reason: 'Add at least one task before closing.' }
  const open = wo.tasks.filter((t) => t.status === 'open')
  if (open.length) {
    return {
      ok: false,
      reason: `${open.length} task${open.length === 1 ? ' is' : 's are'} still open — mark each done, deferred or cancelled.`
    }
  }
  const missingReason = wo.tasks.filter((t) => t.status === 'deferred' && !t.deferReason.trim())
  if (missingReason.length) {
    return { ok: false, reason: 'Every deferred task needs a deferral reason.' }
  }
  return { ok: true, reason: '' }
}

/** WO-2026-0042 */
export function formatWorkOrderNumber(n: number, date: Date): string {
  return `WO-${date.getFullYear()}-${String(n).padStart(4, '0')}`
}
